import React, {
  ChangeEvent,
  FC,
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Decimal from 'decimal.js';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import { toBN } from '@utils/formaters.utils';

import Button from '@components/common/Button';

import { FLOAT_OR_INTEGER_REGEX } from '@constants/common/regex.constants';
import { Variant } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type Props = {
  className?: string;
  classNameInput?: string;
  minLimitValue: Decimal;
  maxLimitValue: Decimal;
  count: Decimal;
  inputValue: Decimal;
  suffix?: string;
  onCount: (value: Decimal) => void;
  onChange: (val: Decimal) => void;
  disabled?: boolean;
  variant?: Variant;
};

const STEP = toBN(1);

const ManageLiquidityCounter: FC<Props> = ({
  className,
  minLimitValue,
  maxLimitValue,
  count,
  onCount,
  classNameInput,
  inputValue,
  onChange,
  suffix,
  disabled,
  variant = Variant.CircleGray,
}) => {
  const { classes, cx } = useStyles();
  const [localValue, setLocalValue] = useState(inputValue.toString());

  useEffect(() => {
    setLocalValue(inputValue.toFixed(2));
  }, [inputValue]);

  const handleIncrement = () => {
    const newValue = Decimal.min(count.plus(STEP), maxLimitValue);
    onCount(newValue);
    onChange(newValue);
  };

  const handleDecrement = () => {
    const newValue = Decimal.max(count.minus(STEP), minLimitValue);
    onCount(newValue);
    onChange(newValue);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (FLOAT_OR_INTEGER_REGEX.test(val) || val === '') {
      setLocalValue(val);
    }
  };

  const handleBlur = () => {
    if (localValue === '' || toBN(localValue).isNaN()) {
      setLocalValue(inputValue.toFixed(2));
      return;
    }
    const numericValue = toBN(localValue);
    const clampedValue = Decimal.max(
      minLimitValue,
      Decimal.min(numericValue, maxLimitValue),
    );
    setLocalValue(clampedValue.toFixed(2));
    onChange(clampedValue);
  };

  const isDisabledIncrement = useMemo(
    () => inputValue.gte(maxLimitValue),
    [inputValue, maxLimitValue],
  );

  const isDisabledDecrement = useMemo(
    () => inputValue.lte(minLimitValue),
    [inputValue, minLimitValue],
  );

  return (
    <div className={cx(classes.box, className)}>
      <div
        className={cx(classes.counterBtn, { [classes.suffixWrap]: !!suffix })}
      >
        {!disabled && (
          <Button
            onClick={handleDecrement}
            disabled={isDisabledDecrement}
            variant={variant}
          >
            <RemoveIcon className={classes.icon} />
          </Button>
        )}
        <div
          className={cx(classes.input, classNameInput, {
            [classes.suffixInput]: !!suffix,
          })}
        >
          <input
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
          />
          {suffix && <div className={classes.suffix}>{suffix}</div>}
        </div>
        {!disabled && (
          <Button
            variant={variant}
            onClick={handleIncrement}
            disabled={isDisabledIncrement}
          >
            <AddIcon className={classes.icon} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default memo(ManageLiquidityCounter);
